import { forwardRef, SelectHTMLAttributes } from 'react'
import { FieldErrors } from 'react-hook-form'
import { cn } from '../../utils'
import FormErrorMessage from './FormErrorMessage'

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  name?: string
  variant: 'semitransparent' | 'white' | 'solid' | 'outline'
  label: string
  errors?: FieldErrors
  selectClassName?: string
  readOnly?: boolean
  activateEmptyWarning?: boolean
  'data-testid': string
}

const Select = forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      id,
      name,
      label,
      variant,
      className,
      selectClassName,
      errors,
      children,
      value,
      readOnly = false,
      disabled = false,
      activateEmptyWarning = false,
      'data-testid': testId,
      ...props
    },
    ref
  ) => {
    const isEmpty = value === null || value === undefined || value === ''

    return (
      <div
        className={cn(
          className,
          activateEmptyWarning &&
            isEmpty &&
            !disabled &&
            'ring-2 ring-red-400 rounded-md'
        )}
        data-testid={`${testId}-wrapper`}
        aria-readonly={readOnly ? 'true' : 'false'}
      >
        <div>
          <select
            id={id}
            name={name}
            className={cn(
              'elm-select',
              { 'pointer-events-none': readOnly },
              selectClassName,
              '!pt-[1rem]'
            )}
            data-variant={variant}
            aria-invalid={!!(name && errors && errors[name])}
            aria-readonly={readOnly ? 'true' : 'false'}
            aria-labelledby={`${id}-label`}
            ref={ref}
            data-testid={testId}
            value={value}
            disabled={disabled || readOnly}
            tabIndex={readOnly ? -1 : undefined}
            {...props}
          >
            {children}
          </select>
          <label
            className={cn(
              'elm-label',
              { 'pointer-events-none': readOnly },
              'text-cool-gray-500'
            )}
            htmlFor={id}
            id={`${id}-label`}
            data-testid={`${testId}-label`}
          >
            {label}
          </label>
        </div>
        {name && errors && (
          <FormErrorMessage
            errors={errors}
            name={name}
            data-testid={`${testId}-error-message`}
          />
        )}
      </div>
    )
  }
)

Select.displayName = 'Select'

export default Select
